/** The scoring module's reads and writes of the API: TanStack Query hooks, and a few plain calls for a form to make as it is filled in. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, isNotFoundError } from '../core/index.js'
import type { ClosePeriodRequest, FacilityScoreRun, PeriodClose, ScoreBreakdown, ScoreHistory, ScorePreview, ScorePreviewRequest, WorkerScore, WorkerStanding } from './types.js'

export function useScoreBreakdown(workerId: string | null) {
  return useQuery({
    queryKey: ['score-breakdown', workerId],
    queryFn: () => apiGet<ScoreBreakdown>(`/workers/${workerId as string}/score/breakdown`),
    enabled: workerId !== null,
  })
}

/** The worker's current running score, computed by the API on first read. */
export function useWorkerScore(workerId: string | null) {
  return useQuery({
    queryKey: ['worker-score', workerId],
    queryFn: () => apiGet<WorkerScore>(`/workers/${workerId as string}/score`),
    enabled: workerId !== null,
  })
}

/** A past period's score (`?period=YYYY-MM`): its final, or its last running score if it never closed. Null when the period has none. */
export function useWorkerScoreForPeriod(workerId: string | null, period: string | null) {
  return useQuery({
    queryKey: ['worker-score', workerId, period],
    queryFn: async (): Promise<WorkerScore | null> => {
      try {
        return await apiGet<WorkerScore>(`/workers/${workerId as string}/score?period=${encodeURIComponent(period as string)}`)
      } catch (error) {
        if (isNotFoundError(error)) {
          return null
        }
        throw error
      }
    },
    enabled: workerId !== null && period !== null,
  })
}

export function useWorkerScoreHistory(workerId: string | null) {
  return useQuery({
    queryKey: ['worker-score-history', workerId],
    queryFn: () => apiGet<ScoreHistory>(`/workers/${workerId as string}/score/history`),
    enabled: workerId !== null,
  })
}

/** Recomputes and stores every worker's running score at the facility; every score view then refetches. */
export function useRecalculateFacilityScores(facilityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiPost<FacilityScoreRun>(`/facilities/${facilityId}/scores/recalculate`, {}),
    onSuccess: () => {
      for (const key of ['workers', 'dashboard-summary', 'tier-distribution', 'worker-profile', 'score-breakdown', 'worker-score', 'worker-score-history', 'worker-standing']) {
        void queryClient.invalidateQueries({ queryKey: [key] })
      }
    },
  })
}

/** Closes a scoring period for the facility: finals, tiers and the new period's dates. Every score and roster view then refetches. */
export function useCloseFacilityPeriod(facilityId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: ClosePeriodRequest) => apiPost<PeriodClose>(`/facilities/${facilityId}/scores/close`, request),
    onSuccess: () => {
      for (const key of ['workers', 'dashboard-summary', 'tier-distribution', 'worker-profile', 'score-breakdown', 'worker-score', 'worker-score-history', 'worker-standing', 'worker-bid-window']) {
        void queryClient.invalidateQueries({ queryKey: [key] })
      }
    },
  })
}

/** What changed settings would do at a facility; stores nothing. */
export function previewFacilityScores(facilityId: string, request: ScorePreviewRequest): Promise<ScorePreview> {
  return apiPost<ScorePreview>(`/facilities/${facilityId}/scores/preview`, request)
}

/** A worker's standing in one call: tier, next tier and points to it, top coaching item. */
export function useWorkerStanding(workerId: string | null) {
  return useQuery({
    queryKey: ['worker-standing', workerId],
    queryFn: () => apiGet<WorkerStanding>(`/workers/${workerId as string}/standing`),
    enabled: workerId !== null,
  })
}
